// Категория: чистые хелперы без побочных эффектов.
// Разбор и подготовка списка форматов, общие для одиночной выдачи и карточек выдачи.

/**
 * Дедуп массива с сохранением порядка.
 * Ключ задаёт вызывающий: у карточки выдачи и одиночного скачивания разные
 * структуры элемента, поэтому общий здесь только механизм.
 * @param items {Array}
 * @param keyOf {Function} - возвращает строковый ключ элемента
 * @returns {Array}
 */
export function dedupeBy(items, keyOf) {
    const seen = new Set();
    return (items || []).filter((item) => {
        const key = keyOf(item);
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
}

/**
 * Числовой вес качества для сортировки.
 * '4K'/'8K' считаем тысячами строк, чтобы они не падали в конец; в остальных случаях
 * берём ведущее число — так '1080p' и '1080p60' весят одинаково, как и было
 * с прежним parseInt(). Метки без ведущих цифр ('source', 'audio') сравнивать
 * не с чем, они уходят в конец.
 * @param value {*} - подпись качества в любом виде
 * @returns {number} вес для сортировки; -Infinity для меток без ведущих цифр
 */
const qualityValue = (value) => {
    const raw = String(value ?? '').trim();

    // хвост после K допустим ('4K60'): без него такая метка падала бы в fallback
    // ниже и весила бы 4, уезжая в конец сортировки вместо начала
    const kilo = raw.match(/^(\d+)\s*K/i);
    if (kilo) {
        return Number(kilo[1]) * 1000;
    }

    const leading = raw.match(/^(\d+)/);
    return leading ? Number(leading[1]) : -Infinity;
};

/**
 * Нормализует подпись качества для показа и для ключа дедупа:
 * срезает только суффикс 'p' у пиксельных меток ('1080p' -> '1080'), а метки другой
 * формы ('4K', '2160p60', 'source') оставляет как есть.
 * @param value {*}
 * @returns {string}
 */
export function normalizeQuality(value) {
    const raw = String(value ?? '').trim();
    const pixels = raw.match(/^(\d+)p$/i);
    return pixels ? pixels[1] : raw;
}

/**
 * Ключ уникальности формата. Общий для обеих выдач: иначе на ответе,
 * где соседствуют '1080' и '1080p', списки качеств разойдутся.
 *
 * noAudio входит в ключ: DASH-ответ отдаёт один ext+quality в версиях со звуком
 * и без — иначе первый по порядку сервера (часто без звука) вытеснит вариант
 * со звуком ещё до sort().
 * @param ext {*} - расширение формата
 * @param quality {*} - подпись качества в любом виде
 * @param noAudio {*}
 * @returns {string}
 */
export function formatKey(ext, quality, noAudio) {
    return `${String(ext || 'mp4').toUpperCase()}|${normalizeQuality(quality)}|${!!noAudio}`;
}

/**
 * Компаратор форматов: сначала со звуком, внутри группы — по убыванию качества.
 * Структура элемента у карточки и одиночной выдачи разная, поэтому доступ к полям
 * задаёт вызывающий.
 * @param accessors {Object}
 * @param accessors.noAudioOf {Function} - возвращает признак «без звука»
 * @param accessors.qualityOf {Function} - возвращает качество формата
 * @returns {Function} компаратор для Array.prototype.sort
 */
export function formatComparator({ noAudioOf, qualityOf }) {
    return (a, b) => {
        const aNoAudio = !!noAudioOf(a);
        const bNoAudio = !!noAudioOf(b);
        if (aNoAudio !== bNoAudio) {
            return aNoAudio ? 1 : -1;
        }

        const aq = qualityValue(qualityOf(a));
        const bq = qualityValue(qualityOf(b));
        return aq === bq ? 0 : bq - aq;
    };
}

/**
 * Приводит сырые форматы к виду, который показывает карточка: расширение, качество,
 * признак «без звука» и сам сырой формат для скачивания.
 *
 * Формат готов только если у него уже есть прямая ссылка либо потоки для
 * локального конвертера. В playlist-ответе первого этапа могут быть только
 * описания качеств без url: они нужны для карточки, но не должны пропускать
 * второй /api/convert, который возвращает реальные ссылки.
 * @param source {Array} - сырые форматы из ответа
 * @returns {Array} отсортированный список без дублей
 */
export function normalizeFormats(source) {
    const formats = (source || [])
        .filter((item) => item
            && !item.audio
            && (item.url || (item.isLocalConverter && item.streams)))
        .map((item) => ({
            noAudio: !!item.no_audio,
            // ext, а не type: в ответе type несёт способ доставки ('mp4 dash',
            // 'm4a audio'), и в подписи качества это лишнее
            ext: (item.ext || item.type || 'mp4').toUpperCase(),
            quality: normalizeQuality(item.quality),
            url: item.url,
            // сырой формат нужен, чтобы скачивать так же, как в одиночном (модалка / прямой линк)
            raw: item,
        }));

    const unique = dedupeBy(formats, (format) => formatKey(format.ext, format.quality, format.noAudio));

    return unique.sort(formatComparator({
        noAudioOf: (format) => format.noAudio,
        qualityOf: (format) => format.quality,
    }));
}

// Форма ответа /api/convert. Вызывающие сравнивают с константами, а не со строками.
export const CONVERT_RESPONSE = {
    // несколько видео — рисуется сеткой карточек
    GRID: 'grid',
    // одно видео: объектом либо завёрнутое в массив длины 1
    SINGLE: 'single',
    // пустой список (актуально для Instagram stories за последние 24 часа)
    EMPTY_LIST: 'empty-list',
    // ни одна из известных форм
    MALFORMED: 'malformed',
};

/**
 * Определяет форму ответа /api/convert.
 *
 * Раньше эти ветки стояли подряд в MainResults.request() и перекрывали друг друга:
 * массив из нескольких элементов без второго url проваливался мимо ветки сетки к
 * разбору одиночного видео и молча показывал только первый элемент, хотя до
 * рефакторинга такой ответ отвергался как битый. Отдельная функция заставляет
 * перечислить формы явно и делает их проверяемыми без компонента.
 *
 * @param data {*} - распарсенное тело ответа
 * @returns {{kind: string, items?: Array, item?: Object}}
 */
export function classifyConvertResponse(data) {
    if (Array.isArray(data)) {
        if (!data.length) {
            return { kind: CONVERT_RESPONSE.EMPTY_LIST };
        }

        if (data.length > 1) {
            // второй элемент со списком форматов — признак того, что это именно
            // список видео, а не что-то другой формы, случайно оказавшееся массивом
            return Array.isArray(data[1]?.url)
                ? { kind: CONVERT_RESPONSE.GRID, items: data }
                : { kind: CONVERT_RESPONSE.MALFORMED };
        }

        return Array.isArray(data[0]?.url)
            ? { kind: CONVERT_RESPONSE.SINGLE, item: data[0] }
            : { kind: CONVERT_RESPONSE.MALFORMED };
    }

    return data && Array.isArray(data.url)
        ? { kind: CONVERT_RESPONSE.SINGLE, item: data }
        : { kind: CONVERT_RESPONSE.MALFORMED };
}

/**
 * Оставляет в многоэлементной выдаче только то, что уместно показывать на вкладке.
 *
 * Instagram reels и stories отдаются как mp4, и прочие типы в их сетке — мусор.
 * Правило жило инлайном в MainResults и мутировало элементы ответа прямо на месте;
 * здесь оно рядом с остальным разбором форматов и ничего не мутирует.
 * @param items {Array} - элементы многоэлементного ответа
 * @param activBtn {string} - активная вкладка Instagram, если есть
 * @returns {Array} элементы без пустых списков форматов
 */
export function filterFormatsForTab(items, activBtn) {
    const mp4Only = ['reels', 'stories'].includes(activBtn);

    return (items || [])
        .map((item) => ({
            ...item,
            url: (item.url || []).filter((format) => !mp4Only || format.type === 'mp4'),
        }))
        .filter((item) => item.url.length);
}

/**
 * Разбирает ответ и отдаёт его тому обработчику, который отвечает за эту форму.
 *
 * Развилка «что делать с каждой формой» стояла копией в обоих компонентах, и это тот
 * же тип дрейфа, из-за которого раньше расходились сами правила классификации.
 * Обработчики у экранов разные по смыслу (для карточки GRID — это отказ, для страницы
 * успех), поэтому общий здесь только перебор форм: отсутствующая ветка попадёт
 * в onMalformed, а не провалится молча.
 *
 * @param data {*} - распарсенное тело ответа
 * @param handlers {Object}
 * @param handlers.onGrid {Function} - (items)
 * @param handlers.onSingle {Function} - (item)
 * @param handlers.onEmptyList {Function} - список пуст; необязателен
 * @param handlers.onMalformed {Function} - форма не распознана либо ветки нет
 * @returns {*} то, что вернул сработавший обработчик
 */
export function dispatchConvertResponse(data, { onGrid, onSingle, onEmptyList, onMalformed }) {
    const response = classifyConvertResponse(data);

    if (response.kind === CONVERT_RESPONSE.GRID && onGrid) {
        return onGrid(response.items);
    }
    if (response.kind === CONVERT_RESPONSE.SINGLE && onSingle) {
        return onSingle(response.item);
    }
    if (response.kind === CONVERT_RESPONSE.EMPTY_LIST && onEmptyList) {
        return onEmptyList();
    }

    return onMalformed(response.kind);
}
